/**
 * Learning Path Gate Prompt - Displays contextual enrollment/progress prompts
 */
'use client';
import { useRouter } from 'next/navigation';
import Button from '../common/button';
import LearningPathEnrollButton from '../common/learning-path-enroll-button';
import {
    isLearningPathCompleted,
    canUnlockLearningPathAssessment,
} from '@/lib/utils/learning-path-utils';

// Assessment Failure Component
interface AssessmentFailureProps {
    correctAnswers: number;
    totalQuestions: number;
    incorrectAnswers: number;
    assessmentScore: number;
    onRetake?: () => void;
}

const AssessmentFailurePrompt = ({
    correctAnswers,
    totalQuestions,
    incorrectAnswers,
    assessmentScore,
    onRetake
}: AssessmentFailureProps) => {
    return (
        <div className="flex flex-col items-center justify-center gap-8 p-8 w-full max-w-[800px] h-[300px] mx-auto" 
             style={{ background: '#30283C' }}>
            {/* Header Section */}
            <div className="flex flex-col items-center gap-1 w-full">
                <h4 className="text-lg font-semibold leading-[120%] text-[rgba(255,255,255,0.98)]">
                    You did not pass the assessment.
                </h4>
                <p className="text-base font-normal leading-[150%] text-[rgba(255,255,255,0.67)]">
                    You answered {correctAnswers} of {totalQuestions} questions correctly
                </p>
            </div>

            {/* Data Points Section */}
            <div className="flex flex-row justify-center items-start gap-4 w-full">
                {/* Total Score */}
                <div className="flex flex-col items-center py-4 gap-1 w-[140px]">
                    <div className="text-2xl font-bold leading-[120%]" style={{ color: '#AC75FF' }}>
                        {correctAnswers}/{totalQuestions}
                    </div>
                    <div className="text-sm font-semibold leading-[120%] text-center text-[rgba(255,255,255,0.85)]">
                        Total Score
                    </div>
                </div>

                {/* Divider */}
                <div className="w-px h-full self-stretch" 
                     style={{ border: '1px solid rgba(255, 255, 255, 0.19)' }} />

                {/* Incorrect */}
                <div className="flex flex-col items-center py-4 gap-1 w-[140px]">
                    <div className="text-2xl font-bold leading-[120%]" style={{ color: '#AC75FF' }}>
                        {incorrectAnswers}
                    </div>
                    <div className="text-sm font-semibold leading-[120%] text-center text-[rgba(255,255,255,0.85)]">
                        Incorrect
                    </div>
                </div>

                {/* Divider */}
                <div className="w-px h-full self-stretch" 
                     style={{ border: '1px solid rgba(255, 255, 255, 0.19)' }} />

                {/* Assessment Score */}
                <div className="flex flex-col items-center py-4 gap-1 w-[140px]">
                    <div className="text-2xl font-bold leading-[120%]" style={{ color: '#AC75FF' }}>
                        {assessmentScore}%
                    </div>
                    <div className="text-sm font-semibold leading-[120%] text-center text-[rgba(255,255,255,0.85)]">
                        Assessment Score
                    </div>
                </div>
            </div>

            {/* Button Section */}
            <div className="flex flex-row justify-center items-start gap-4">
                <button
                    onClick={onRetake}
                    className="flex flex-row justify-center items-center px-3 py-4 gap-2 h-10 text-sm font-medium leading-[140%]"
                    style={{ 
                        background: '#AC75FF', 
                        color: 'rgba(26, 25, 25, 0.98)'
                    }}
                >
                    Retake assessment
                </button>
            </div>
        </div>
    );
};

// Prompt content for different enrollment states
const PromptContent = {
    isEnrolled: {
        title: "You’re enrolled!",
        description: "You have successfully enrolled in <strong>{Course Title}</strong> learning path.",
        buttonTitle: "Start Learning",
        buttonUrl: "/learning-paths"
    },
    isNotEnrolled: {
        title: "You are not currently enrolled.",
        description: "Click the button below to start tracking your <strong>{Course Title}</strong> progress.",
        buttonTitle: "Enroll Now",
        buttonUrl: "#"
    },
    keepLearning: {
        title: "Keep learning!",
        description: "Choose content on the left or click below to continue learning.",
        buttonTitle: "Resume Learning",
        buttonUrl: "/learning-paths"
    },
    learningCompleted: {
        title: "You've completed!",
        description: "You've completed all content in this learning path. Click below to review or continue.",
        buttonTitle: "Completed",
        buttonUrl: "/learning-paths"
    },
    assessment_incomplete: {
        title: "Please complete your learning to unlock the assessment",
        description: "You’re almost there! To take the final assessment, you must complete all required courses in this learning path.",
        buttonTitle: "Continue learning",
        buttonUrl: "#",
    },
    assessment_complete: {
        title: "You’re ready to go!",
        description: "Take the assessment and earn your Contentstack Developer Certificate.",
        buttonTitle: "Take assessment",
        buttonUrl: "#"
    }

}

interface LPGatePromptProps {
    learningPathUid?: string;
    learningPathUrl?: string;
    isSubscribed?: boolean;
    hasStarted?: boolean;
    isCompleted?: boolean;
    /** Every LP course completed or at 100% (merged progress). Used with LP % / isCompleted to unlock assessment. */
    allCoursesCompleted?: boolean;
    progressPercent?: number;
    isAssessmentView?: boolean;
    learningPathTitle?: string;
    resumeUrl?: string; // URL to resume learning from
    /** When false, do not router.push after enroll/start (e.g. stay on LP assessment URL). */
    enrollAutoNavigate?: boolean;
    // Assessment failure props
    assessmentFailed?: boolean;
    assessmentData?: {
        correctAnswers: number;
        totalQuestions: number;
        incorrectAnswers: number;
        assessmentScore: number;
    };
    onRetakeAssessment?: () => void;
}

/** Determines prompt type based on user's enrollment and progress state */
const getPromptType = (
    isSubscribed: boolean,
    hasStarted: boolean,
    isAssessmentView: boolean,
    isCompleted: boolean | undefined,
    progressPercent?: number,
    allCoursesCompleted?: boolean
) => {
    const completed = isLearningPathCompleted(isCompleted, progressPercent);
    const canTakeAssessment = canUnlockLearningPathAssessment({
        allCoursesCompleted: allCoursesCompleted === true,
        lpProgressPercent: progressPercent,
        lpIsCompleted: isCompleted,
    });
    if (isAssessmentView && isSubscribed) {
        return canTakeAssessment ? "assessment_complete" : "assessment_incomplete";
    }
    if (isSubscribed && hasStarted) {
        return completed ? "learningCompleted" : "keepLearning";
    }
    if (!isSubscribed) return "isNotEnrolled";
    return "isEnrolled";
}

const LPGatePrompt = ({
    learningPathUid,
    learningPathUrl,
    isSubscribed = false,
    hasStarted = false,
    isCompleted,
    allCoursesCompleted = false,
    progressPercent,
    isAssessmentView = false,
    learningPathTitle,
    resumeUrl,
    enrollAutoNavigate = true,
    assessmentFailed = false,
    assessmentData,
    onRetakeAssessment,
}: LPGatePromptProps) => {
    const router = useRouter();

    // Show assessment failure prompt if assessment was failed
    if (assessmentFailed && assessmentData) {
        return (
            <AssessmentFailurePrompt
                correctAnswers={assessmentData.correctAnswers}
                totalQuestions={assessmentData.totalQuestions}
                incorrectAnswers={assessmentData.incorrectAnswers}
                assessmentScore={assessmentData.assessmentScore}
                onRetake={onRetakeAssessment}
            />
        );
    }

    const canUnlockLpAssessment = canUnlockLearningPathAssessment({
        allCoursesCompleted,
        lpProgressPercent: progressPercent,
        lpIsCompleted: isCompleted,
    });

    const promptType = getPromptType(isSubscribed, hasStarted, isAssessmentView, isCompleted, progressPercent, allCoursesCompleted);
    const content = PromptContent[promptType];
    const hasButton = 'buttonTitle' in content && 'buttonUrl' in content;

    // Handle assessment navigation
    const handleAssessmentClick = () => {
        if (learningPathUrl) {
            router.push(`/learning-paths${learningPathUrl}/assessment`);
        }
    };

    return (
        <div className="flex flex-col items-center justify-center h-full py-16 lg:px-6 text-center">
            <h3 className="cs-h2 mb-2">{content.title}</h3>
            <p
                className="font-inter text-lg leading-[150%] tracking-normal text-center text-text-default-light mb-8 lg-gate-prompt-description"
                dangerouslySetInnerHTML={{
                    __html: learningPathTitle
                        ? content.description.replace("{Course Title}", learningPathTitle)
                        : content.description
                }}
            />
            {hasButton && (
                promptType === 'assessment_complete' ? (
                    <Button 
                        title={content.buttonTitle} 
                        url="#" 
                        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                            e.preventDefault();
                            handleAssessmentClick();
                        }} 
                    />
                ) : (
                    <LearningPathEnrollButton
                        learningPathUid={learningPathUid}
                        initialIsSubscribed={isSubscribed}
                        initialHasStarted={hasStarted}
                        progressPercent={progressPercent}
                        isCompleted={isCompleted}
                        isLearningMaterialCompleted={canUnlockLpAssessment}
                        hasAssessment={isAssessmentView}
                        navigateUrl={resumeUrl || learningPathUrl || '/learning-paths'}
                        autoNavigate={enrollAutoNavigate}
                        directHover
                        variant="PrimaryFillButton"
                        className="min-w-[120px] justify-center btn-icon-move"
                        buttonText={{
                            notEnrolled: content.buttonTitle,
                            enrolled: content.buttonTitle,
                            enrolledAndStarted: content.buttonTitle,
                        }}
                    />
                )
            )}
        </div>
    );
};

export default LPGatePrompt;
export { AssessmentFailurePrompt };
